/**
 * Agents that need maintenance (crewdle-sdk-firebase functions/app
 * libs/agent-maintenance.ts): OpenClaw refuses to start the agent's workspace
 * after the automatic migration. The agent's messages are held; it is retried
 * automatically once per new gateway version and every 6 h; Crewdle staff can
 * retry it now (`retryAgentMaintenance`, /restart with force). Rows come from
 * the staff-only `listMaintenanceAgents` callable of the region's project.
 */
export interface MaintenanceAgentRow {
  agentId: string;
  agentName: string;
  organizationId: string;
  organizationName: string;
  /** 'maintenance', or 'restarting' while a recovery revive runs. */
  status: string;
  /** ISO times. */
  since: string | null;
  lastAttemptAt: string | null;
  /** 'version' | 'interval' | 'staff' — what started the last attempt. */
  lastAttemptReason: string | null;
  /** 'gateway-maintenance-required' (the pool's /status verdict) | 'maintenance-required' (a refused restart). */
  errorCode: string;
  /** 'revive-failed' when the last attempt failed some other way. */
  lastError: string | null;
  gatewayVersion: string | null;
  attempts: number;
  podId: string | null;
}

export interface MaintenanceAgentList {
  region: string;
  projectId: string;
  agents: MaintenanceAgentRow[];
}

/**
 * Crewdle staff: a VERIFIED @crewdle.com account — the same rule as the
 * server's staff-only callables and the Firestore rules (an unverified account
 * can set any address).
 */
export function isCrewdleStaff(
  user: { email?: string | null; emailVerified?: boolean } | null | undefined,
): boolean {
  return !!user?.emailVerified && !!user.email && user.email.toLowerCase().endsWith('@crewdle.com');
}

/** A recovery revive of this agent is running: no second "Retry now". */
export function isRetrying(row: Pick<MaintenanceAgentRow, 'status'>): boolean {
  return row.status === 'restarting';
}
